import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { getValidImageUrl, SOURCES } from 'client/utils/image-helpers';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { getAuthorMetadata } from 'site-modules/shared/utils/editorial/author';
import { Share } from 'site-modules/shared/components/share/share';
import { AuthorName } from 'site-modules/shared/components/editorial/author-name/author-name';
import { AuthorTooltip } from 'site-modules/shared/components/editorial/author-tooltip/author-tooltip';
import { ArticleDateline } from './article-dateline';

import './article-metadata.scss';

export function ArticleMetadata({
  content = DEFAULT_CONTENT,
  allAuthors = {
    author: DEFAULT_CONTENT,
    coAuthor: DEFAULT_CONTENT,
    editor: DEFAULT_CONTENT,
  },
  isLegacy = false,
  className = null,
  listClassName = null,
  rssLink = null,
  creativeId = null,
  isMobile = false,
}) {
  const legacyHasAuthorData = isLegacy && content?.authors?.[0]?.name;
  const researchHasAuthorData = !isLegacy && allAuthors?.author?.hasMetadata('authorName');
  const canonical = getCanonical(content);

  if (!legacyHasAuthorData && !researchHasAuthorData) {
    return null;
  }

  const { updated, published } = content;
  const authors = isLegacy
    ? [{ name: content.authors[0].name, byline: 'written by' }]
    : [
        { ...getAuthorMetadata(allAuthors?.author), byline: 'written by' },
        { ...getAuthorMetadata(allAuthors?.coAuthor), byline: 'and' },
        { ...getAuthorMetadata(allAuthors?.editor), byline: 'edited by' },
      ].filter(authorData => authorData?.name);

  return (
    <div className={classnames('article-metadata d-flex', className)} data-tracking-parent={creativeId}>
      <ul
        className={classnames(
          'article-metadata-list d-flex flex-wrap list-unstyled align-items-center justify-content-center mb-0',
          listClassName
        )}
      >
        <li className="author d-flex align-items-start">
          {authors
            .filter(authorData => authorData?.avatar)
            .map(({ avatar, name }) => (
              <img
                key={`${avatar}-${name}`}
                className="author-photo rounded mt-0_25 me-0_5"
                src={getValidImageUrl(avatar, SOURCES.STATIC)}
                alt=""
                height={45}
                width={45}
                loading="lazy"
              />
            ))}
          <div className="author-lines small">
            {authors.map(({ link, name, byline, title, aboutAuthor }) => (
              <div key={name} className="text-nowrap">
                {byline} <AuthorName name={name} link={link} className="p-0 small fw-bold rounded" />
                {!!aboutAuthor && <AuthorTooltip aboutAuthor={aboutAuthor} name={name} title={title} />}
              </div>
            ))}
            {isMobile && <ArticleDateline updated={updated} published={published} />}
          </div>
        </li>
        {!isMobile && (
          <li className="date separators pos-r ps-0_75 px-md-1 py-0_25">
            <ArticleDateline updated={updated} published={published} />
          </li>
        )}
        <li className={classnames('share text-center mx-auto', { 'w-100': isMobile })}>
          <Share
            url={canonical}
            title={content.title}
            rssLink={rssLink}
            iconClassName="px-0_5 py-0_25 text-cool-gray-40"
            labelClassName="d-none"
          />
        </li>
      </ul>
    </div>
  );
}

ArticleMetadata.propTypes = {
  content: CmsEntities.Content,
  allAuthors: PropTypes.shape({
    author: CmsEntities.Content,
    coAuthor: CmsEntities.Content,
    editor: CmsEntities.Content,
  }),
  className: PropTypes.string,
  listClassName: PropTypes.string,
  rssLink: PropTypes.string,
  creativeId: PropTypes.string,
  isLegacy: PropTypes.bool,
  isMobile: PropTypes.bool,
};
